/**
 * Possible root layout IDs for the application. Used to determine theming and other layout-related features.
 */
export type RootLayoutId = 'maintainer' | 'maintainer-high-contrast' | 'consumer' | 'consumer-high-contrast';

/**
 * Determines the root layout ID based on the current path and accessibility setting.
 * @returns {RootLayoutId} The root layout ID.
 */
export function getRootlayoutId(): RootLayoutId {
  let idValue: RootLayoutId;

  const path = window.location.pathname;
  if (path.startsWith('/maintainer')) {
    // INFO: The maintainer does not have an accessibility mode yet.
    // if (APP_CONFIG.ACCESSIBILITY.ENABLED) {
    //   return 'maintainer-high-contrast';
    // }
    idValue = 'maintainer';
  } else {
    if (APP_CONFIG.ACCESSIBILITY.ENABLED) {
      idValue = 'consumer-high-contrast';
    } else {
      idValue = 'consumer';
    }
  }

  // This is a hack to also include radix components in the root layout ID.
  document.body.id = idValue;

  return idValue;
}

const APP_CONFIG = {
  LINKS: {
    SERVICE_PORTAL_LINK:
      'https://myit.siemens.com/myitportal?id=create_incident&deeplink=-dl-nGXnfkAIrZk4Jf4m9Lg3kMYdEPYi',
    TUTORIAL_LINK: 'https://mylearningworld.siemens.com/web/de/app/knowledge-board/lex_auth_014348004935442432193',
  },
  /**
   * Controls the behaviour of the maintener part of the app. Although some of these also need to be supported by the backend.
   */
  MAINTAINER: {
    BUC_ENABLED_NODES: [
      'UniversalDesignObligation',
      'UniversalBuildingBlock',
      'UniversalDataBlock',
      'UniversalMaturityStage',
      'UniversalCompetency',
    ],
    OP_ENABLED_NODES: ['UniversalDesignObligation', 'UniversalBuildingBlock'],
  },

  /**
   * Configuration object to be passed to MSAL instance on creation.
   * For a full list of MSAL.js configuration parameters, visit:
   * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
   */
  MSAL: {
    auth: {
      clientId: import.meta.env.VITE_MS_CLIENT_ID, // application id
      // tenant id
      authority: `https://login.microsoftonline.com/${import.meta.env.VITE_MS_TENANT_ID}`,
      redirectUri: import.meta.env.VITE_MS_REDIRECT_URI, // Points to window.location.origin. You must register this URI on Azure Portal/App Registration.
      postLogoutRedirectUri: '/', // Indicates the page to navigate after logout.
      navigateToLoginRequestUrl: false, // If "true", will navigate back to the original request location before processing the auth code response.
    },
    cache: {
      cacheLocation: 'sessionStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
      storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
    },

    // Uncomment the "system" key to initialize the logger. Otherwise, TS will complain.
    // system: {
    //   loggerOptions: {
    //     loggerCallback: (level: LogLevel, containsPii: boolean) => {
    //       if (containsPii) {
    //         return;
    //       }
    //       switch (level) {
    //         case LogLevel.Error:
    //           // console.error(message);
    //           return;
    //         case LogLevel.Info:
    //           // console.info(message);
    //           return;
    //         case LogLevel.Verbose:
    //           // console.debug(message);
    //           return;
    //         case LogLevel.Warning:
    //           // console.warn(message);
    //           return;
    //         default:
    //           return;
    //       }
    //     },
    //   },
    // },
  },
  /**
   * Scopes you add here will be prompted for user consent during sign-in.
   * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
   * For more information about OIDC scopes, visit:
   * https://docs.microsoft.com/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
   */
  LOGIN_REQUEST: {
    scopes: [],
  },
  TOKEN_REQUEST: {
    scopes: [`api://${import.meta.env.VITE_MS_CLIENT_ID}/consumer`],
  },

  ACCESSIBILITY: {
    // INFO: This is hard-coded for now and might be done with a theme switcher in the future.
    // It's a global switch to enable or disable accessibility features.
    ENABLED: true,
  },
};

export default APP_CONFIG;
